import React, { useCallback, useEffect, useRef, useState } from "react";

interface Product {
  curriculum: string;
  subject:    string;
  prod_desc:  string;
  prod_id:    string;
}

interface AlertState {
  visible:  boolean;
  message:  string;
  type:     "E" | "A" | "I" | "Q"; // mirrors pMesgType in gfn_ShowMessage
  onOk?:    () => void;
}


const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

async function fetchProducts(schoolCode: string, products: string) {
  const res = await fetch(
    `${API_BASE_URL}api/select-products/webadmin?school_code=${encodeURIComponent(schoolCode)}&products=${encodeURIComponent(products)}`
  );
  if (!res.ok) throw new Error("Failed to load products");
  return res.json() as Promise<{ products: Product[]; selected: string; error: string }>;
}


function AlertBox({
  alert,
  onClose,
}: {
  alert: AlertState;
  onClose: () => void;
}) {
  if (!alert.visible) return null;

  const titleMap = { E: "Error", A: "Warning", I: "Information", Q: "Confirmation" };
  const title = titleMap[alert.type] ?? "Warning";
  // mirrors: pMesgType == 'E' → red title bar, else blue-ish
  const titleBg = alert.type === "E" ? "#cc0000" : "#2f6089";

  return (
    <>
      {/* mirrors: g_Opaque overlay */}
      <div style={{
        position: "fixed", inset: 0,
        backgroundColor: "#cccccc",
        opacity: 0.6,
        zIndex: 99998,
      }} />
      {/* mirrors: g_ErrorBox */}
      <div style={{
        position: "fixed",
        top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: 506, minHeight: 180,
        backgroundColor: "white",
        zIndex: 99999,
        fontFamily: "Tahoma",
        fontSize: 14,
        boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
        overflow: "hidden",
      }}>
        {/* Title bar — mirrors the move-handle <tr> */}
        <div style={{
          background: titleBg,
          color: "white",
          padding: "6px 10px",
          fontFamily: "Tahoma",
          fontSize: 13,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "default",
        }}>
          <span>{title}</span>
        </div>
        {/* Message */}
        <div style={{ padding: "20px 24px 10px", color: "#525252", minHeight: 70 }}
          dangerouslySetInnerHTML={{ __html: alert.message.replace(/\n/g, "<br/>") }}
        />
        {/* OK button — mirrors ok_n.png image button */}
        <div style={{ textAlign: "center", padding: "10px 0 16px" }}>
          <button
            onClick={() => { onClose(); alert.onOk?.(); }}
            style={{
              backgroundColor: "#119ECC",
              color: "white",
              border: "none",
              borderRadius: 3,
              padding: "6px 28px",
              fontFamily: "Trebuchet MS",
              fontSize: "10pt",
              cursor: "pointer",
              width: 80,
              height: 28,
            }}
          >
            OK
          </button>
        </div>
      </div>
    </>
  );
}


export default function EL_SelectProducts() {
  // Read query string — mirrors Request.QueryString
  const qp        = new URLSearchParams(window.location.search);
  const schoolCode = qp.get("school_code") ?? qp.get("SchoolCode") ?? "";
  const productsQS = qp.get("products")    ?? qp.get("Products")    ?? "";

  const [rows,       setRows]       = useState<Product[]>([]);
  const [selected,   setSelected]   = useState<Set<string>>(new Set()); // Set of prod_id
  const [loading,    setLoading]    = useState(true);
  const [alert,      setAlert]      = useState<AlertState>({ visible: false, message: "", type: "A" });

  const tableWrapRef = useRef<HTMLDivElement>(null);
  const btnAreaRef   = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function resize() {
      if (!tableWrapRef.current || !btnAreaRef.current) return;
      const btnH = btnAreaRef.current.offsetHeight;
      tableWrapRef.current.style.height = (window.innerHeight - btnH - 25) + "px";
      tableWrapRef.current.style.width  = (window.innerWidth  - 15) + "px";
    }
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [rows]);

  useEffect(() => {
    fetchProducts(schoolCode, productsQS)
      .then((data) => {
        setRows(data.products);

        if (data.selected) {
          const preSelected = data.selected.split(",").map((s) => s.trim()).filter(Boolean);
          setSelected(new Set(preSelected));
        }

        // mirrors: gfn_ShowMessage("<%=ErrorMesg%>", 'E')  — show if non-empty
        if (data.error) {
          setAlert({ visible: true, message: data.error, type: "E" });
        }
      })
      .catch((e) => setAlert({ visible: true, message: e.message, type: "E" }))
      .finally(() => setLoading(false));
  }, [schoolCode, productsQS]);

  const toggleRow = useCallback((prodId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(prodId)) next.delete(prodId);
      else next.add(prodId);
      return next;
    });
  }, []);

  function getSelectedValues(colName: "prod_desc" | "prod_id" | "subject" | "curriculum"): string {
    return rows
      .filter((r) => selected.has(r.prod_id))
      .map((r) => r[colName])
      .join(",");
  }
  function checkCurriculum(): { ok: boolean; curriculum: string } {
    const curricula = rows
      .filter((r) => selected.has(r.prod_id))
      .map((r) => r.curriculum);
    const unique = [...new Set(curricula)];
    if (unique.length > 1) return { ok: false, curriculum: "" };
    return { ok: true, curriculum: unique[0] ?? "" };
  }


  const handleOk = useCallback(() => {
    if (selected.size === 0) {
      setAlert({ visible: true, message: "Please select product(s).", type: "A" });
      return;
    }

    // mirrors: fn_CheckCurriculum()
    const { ok, curriculum } = checkCurriculum();
    if (!ok) {
      setAlert({
        visible: true,
        message: "Package should have product(s) from same curriculum",
        type: "A",
      });
      return;
    }

    const retValue = [
      getSelectedValues("prod_desc"),
      getSelectedValues("prod_id"),
      getSelectedValues("subject"),
      curriculum,
    ].join(",,");

    if (window.opener) {
      window.opener.postMessage({ type: "EL_SelectProducts_Return", value: retValue }, "*");
    }
    window.close();
  }, [selected, rows]);

  const handleCancel = useCallback(() => window.close(), []);

  return (
    <>
      {/* CSS mirrors eLearnCSS.css classes used in clsGrid output */}
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; font-family: 'Trebuchet MS', sans-serif; font-size: 10pt; }

        /* mirrors .cssGridViewHeader */
        .cssGridViewHeader {
          font-family: 'Trebuchet MS', sans-serif;
          font-size: 8pt;
          background-color: #2f6089;
          color: white;
          font-weight: normal;
          padding: 4px 6px;
          text-align: left;
          border: 1px solid #888;
          white-space: nowrap;
        }

        /* mirrors .cssGridRow */
        .cssGridRow {
          font-family: 'Trebuchet MS', sans-serif;
          font-size: 8pt;
          background-color: white;
          vertical-align: text-top;
        }
        .cssGridRow td {
          padding: 3px 6px;
          border: 1px solid #ccc;
          text-align: left;
        }

        /* mirrors .cssGridAltRow */
        .cssGridAltRow {
          font-family: 'Trebuchet MS', sans-serif;
          font-size: 8pt;
          background-color: #EFF3FB;
        }
        .cssGridAltRow td {
          padding: 3px 6px;
          border: 1px solid #ccc;
          text-align: left;
        }

        /* mirrors .cssGridSelRow — selected row */
        .cssGridSelRow {
          font-family: 'Trebuchet MS', sans-serif;
          font-size: 8pt;
          background-color: orange;
          color: white;
        }
        .cssGridSelRow td {
          padding: 3px 6px;
          border: 1px solid #ccc;
          text-align: left;
        }

        /* mirrors lst_divScrollableTable — scrollable container */
        #lst_divScrollableTable {
          overflow: auto;
          border: solid 1px #A0A0A0;
        }

        /* mirrors .cssDivForGrid */
        .cssDivForGrid {
          width: 100%;
          border: solid 1px #A0A0A0;
          border-top-style: none;
        }

        /* mirrors .cssContentTitle */
        .cssContentTitle {
          background-color: #2f6089;
          color: white;
          height: 22px;
          font-size: 9pt;
          font-family: 'Trebuchet MS', sans-serif;
          padding-left: 7px;
          line-height: 22px;
        }

        /* mirrors .cssButton */
        .cssButton {
          background-color: #119ECC;
          font-family: 'Trebuchet MS', sans-serif;
          font-size: 8pt;
          color: white;
          border-style: none;
          height: 20px;
          width: 80px;
          cursor: pointer;
        }

        table.grid-table {
          width: 100%;
          border-collapse: collapse;
        }

        tr.grid-row:hover td {
          background-color: #d0e4f7;
          cursor: pointer;
        }
        tr.cssGridSelRow:hover td {
          background-color: #0000cc;
        }
      `}</style>

      {/* mirrors: <div style="padding:2px; margin:0px;"> <table> <tr> <td>ProductTable</td> */}
      <div style={{ padding: 2, margin: 0 }}>
        <table border={0} cellPadding={5} cellSpacing={0} style={{ width: "100%" }}>
          <tbody>
            <tr>
              <td>
                {/* mirrors: clsGrid title bar — cssContentTitle */}
                <div className="cssContentTitle">
                  Select Product(s) for required Package
                </div>

                {/* mirrors: lst_divScrollableTable — the scrollable grid container */}
                <div id="lst_divScrollableTable" ref={tableWrapRef}>
                  {loading ? (
                    <div style={{ padding: 10, fontFamily: "Trebuchet MS", fontSize: "8pt" }}>
                      Loading…
                    </div>
                  ) : rows.length === 0 ? (
                    <div style={{ padding: 10, fontFamily: "Trebuchet MS", fontSize: "8pt" }}>
                      No products found.
                    </div>
                  ) : (
                    /*
                     * clsGrid.ConstructScrollableTable output:
                     *   SelectionMode = CHECKBOXES  → checkbox in col 0
                     *   GridLine      = BOTH        → border-collapse with borders
                     *   SerialNumber  = False       → no row number column
                     *   Headers: Curriculum | Subject | Product Description | ProductID
                     *   Align: left | left | left | left
                     */
                    <table className="grid-table">
                      <thead>
                        <tr>
                          {/* checkbox header cell — mirrors clsGrid CHECKBOXES header */}
                          <th className="cssGridViewHeader" style={{ width: 28 }}></th>
                          <th className="cssGridViewHeader">Curriculum</th>
                          <th className="cssGridViewHeader">Subject</th>
                          <th className="cssGridViewHeader">Product Description</th>
                          <th className="cssGridViewHeader">ProductID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, idx) => {
                          const isSelected = selected.has(row.prod_id);
                          const rowClass = isSelected
                            ? "cssGridSelRow"
                            : idx % 2 === 0
                            ? "cssGridRow"
                            : "cssGridAltRow";

                          return (
                            <tr
                              key={row.prod_id}
                              className={`grid-row ${rowClass}`}
                              onClick={() => toggleRow(row.prod_id)}
                            >
                              {/* mirrors: checkbox column from CHECKBOXES SelectionMode */}
                              <td style={{ textAlign: "center", width: 28 }}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleRow(row.prod_id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td>{row.curriculum}</td>
                              <td>{row.subject}</td>
                              <td>{row.prod_desc}</td>
                              <td>{row.prod_id}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        id="divbuttons"
        ref={btnAreaRef}
        style={{ textAlign: "center", paddingTop: 8 }}
      >

        <input
          id="btnOk"
          type="button"
          value="OK"
          onClick={handleOk}
          style={{ width: 75, cursor: "pointer" }}
        />
        &nbsp;&nbsp;

        <input
          id="btnCancel"
          type="button"
          value="Cancel"
          onClick={handleCancel}
          style={{ width: 75, cursor: "pointer" }}
        />
      </div>

      {/* mirrors: gfn_ShowMessage() custom alert box */}
      <AlertBox alert={alert} onClose={() => setAlert((a) => ({ ...a, visible: false }))} />
    </>
  );
}